import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import {
  listNotifications,
  markAllRead,
  markRead,
  unreadCount,
  type NotificationOut,
} from "../api/notifications";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { formatMoscowDate } from "../utils/datetime";

const POLL_MS = 60_000;

interface Props {
  tone?: "light" | "dark"; // dark = inside sidebar
}

export function NotificationBell({ tone = "light" }: Props) {
  const { user } = useCurrentUser();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationOut[]>([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const refreshCount = useCallback(async () => {
    if (!user) return;
    try {
      const r = await unreadCount();
      setCount(r.unread_count);
    } catch {
      // silent — auth or network; polling will retry
    }
  }, [user]);

  // Polling
  useEffect(() => {
    if (!user) return;
    void refreshCount();
    const t = setInterval(() => void refreshCount(), POLL_MS);
    return () => clearInterval(t);
  }, [user, refreshCount]);

  // Outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function handleOpen() {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    setLoading(true);
    try {
      const r = await listNotifications({ limit: 30 });
      setItems(r.items);
      setCount(r.unread_count);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  async function handleMarkOne(id: number) {
    try {
      await markRead(id);
      setItems((arr) =>
        arr.map((n) =>
          n.id === id ? { ...n, read_at: new Date().toISOString() } : n,
        ),
      );
      setCount((c) => Math.max(0, c - 1));
    } catch {
      // silent
    }
  }

  async function handleMarkAll() {
    try {
      await markAllRead();
      setItems((arr) =>
        arr.map((n) => ({
          ...n,
          read_at: n.read_at ?? new Date().toISOString(),
        })),
      );
      setCount(0);
    } catch {
      // silent
    }
  }

  if (!user) return null;

  const buttonClass =
    tone === "dark"
      ? "relative grid h-8 w-8 place-items-center rounded-lg text-white/75 transition hover:bg-white/10 hover:text-white"
      : "relative grid h-9 w-9 place-items-center rounded-lg text-ink-soft transition hover:bg-surface-alt";
  const badgeClass =
    "absolute right-1 top-1 grid min-w-[16px] place-items-center rounded-full bg-brand-danger px-1 text-[10px] font-bold leading-[14px] text-white";

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={handleOpen}
        aria-label="Уведомления"
        className={buttonClass}
      >
        <Bell size={tone === "dark" ? 16 : 18} strokeWidth={2} />
        {count > 0 && (
          <span aria-hidden className={badgeClass}>
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Уведомления"
          className="absolute right-0 z-30 mt-2 w-80 overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-line"
        >
          <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <div className="text-sm font-semibold text-ink">Уведомления</div>
            {count > 0 && (
              <button
                type="button"
                onClick={handleMarkAll}
                className="text-xs font-medium text-brand hover:underline"
              >
                Отметить всё
              </button>
            )}
          </div>
          {loading ? (
            <div className="px-4 py-6 text-center text-xs text-ink-muted">
              Загрузка…
            </div>
          ) : items.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-ink-muted">
              Нет уведомлений
            </div>
          ) : (
            <ul className="max-h-[420px] overflow-y-auto">
              {items.map((n) => (
                <NotificationRow
                  key={n.id}
                  item={n}
                  onMark={() => handleMarkOne(n.id)}
                  onClose={() => setOpen(false)}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationRow({
  item: n,
  onMark,
  onClose,
}: {
  item: NotificationOut;
  onMark: () => void;
  onClose: () => void;
}) {
  const created = new Date(n.created_at);
  const relative = relativeTime(created);
  const isUnread = n.read_at === null;
  const content = (
    <div
      className={`relative flex items-start gap-2 border-b border-line px-4 py-3 last:border-b-0 ${
        isUnread ? "bg-brand/5" : ""
      }`}
    >
      {isUnread && (
        <span aria-hidden className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
      )}
      <div className={`min-w-0 flex-1 ${isUnread ? "" : "pl-3.5"}`}>
        <div className="truncate text-sm font-medium text-ink">{n.title}</div>
        {n.body && (
          <div className="mt-0.5 line-clamp-2 text-xs text-ink-soft">{n.body}</div>
        )}
        <div className="mt-1 text-[10px] uppercase tracking-wide text-ink-muted">
          {relative}
        </div>
      </div>
      {isUnread && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onMark();
          }}
          aria-label="Отметить прочитанным"
          className="grid h-6 w-6 shrink-0 place-items-center rounded text-ink-muted transition hover:bg-surface-alt hover:text-ink"
        >
          <Check size={12} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );

  if (n.link_url) {
    return (
      <li>
        <Link
          to={n.link_url}
          onClick={() => {
            if (n.read_at === null) onMark();
            onClose();
          }}
          className="block hover:bg-surface-alt/40"
        >
          {content}
        </Link>
      </li>
    );
  }
  return <li>{content}</li>;
}

function relativeTime(date: Date): string {
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 60) return "только что";
  if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} дн назад`;
  return formatMoscowDate(date);
}
